// Register the user-facing `knob(n, channel?)` signal.
//
// `knob(N)` returns a Strudel signal pattern whose value at any cycle
// time is the latest-seen MIDI CC value for controller N (normalised to
// 0..1). User code reaches it via globalThis at evaluate-time, exactly
// like `samples` and `getSound` — superdough already exposes the rest
// of its surface that way, so we follow the convention.
//
// Why not name it `cc`?
//   `cc()` is taken: Strudel uses it as an *output* control
//   (`note(...).cc(21, 64)` sends MIDI CC out). Shadowing that with an
//   input would silently break existing patterns. `knob(N)` is the
//   input side; output stays `cc`.
//
// The signal re-queries the CC state map on every event the scheduler
// generates, so a knob twist is felt within one Strudel query window
// (~50 ms) without needing a repl.evaluate() — exactly the model
// outlined in the plan spike.

import { getCcValue } from '../midi/ccState';
import { getStrudelModule } from './player';
import type { Pattern } from './types';

let registered = false;

/** Idempotent. Called from player.ts after initStrudel resolves. */
export function registerKnobSignal(): void {
  if (registered) return;
  const mod = getStrudelModule();
  // Strudel's `signal` is the load-bearing primitive; if it's not on
  // the module export, fall through to globalThis (initStrudel installs
  // many primitives globally).
  const signal =
    mod?.signal ??
    (globalThis as { signal?: (fn: (t: number) => number) => Pattern }).signal;
  if (typeof signal !== 'function') return;

  const knob = (n: number, channel: number = 0): Pattern => {
    return signal(() => {
      const v = getCcValue(channel, n);
      return v ?? 0;
    });
  };

  // Place on globalThis so user code can reference it inside the
  // Strudel evaluator. Refusing to overwrite an existing `knob` keeps
  // us safe if a future Strudel release ships its own.
  const g = globalThis as { knob?: typeof knob };
  if (typeof g.knob === 'function') {
    registered = true;
    return;
  }
  g.knob = knob;
  registered = true;
}

/** True once the global has been installed. UI / docs use this to
 *  conditionally show the MIDI affordance. */
export function isKnobSignalRegistered(): boolean {
  return registered;
}

/** For tests — wipe state. Do not call from app code. */
export function _resetKnobRegistration(): void {
  registered = false;
  delete (globalThis as { knob?: unknown }).knob;
}
